import React, { useEffect, useRef, useState } from "react";
import ImageComponent from "../ui/ImageComponent";

interface HeaderComicProps {
  title: string;
  banner: { path?: string } | string;
  description?: string;
  playUrl?: string;
  onPlay?: (url?: string) => void;
  type: "comic" | "novel";
  firstChapterId?: string;
  lastChapterId?: string;
  loading?: boolean;
  author?: string;
  genre?: string[];
  status?: string;
  rating?: number;
  totalChapters?: number;
  views?: number;
  episodeTotal?: number;
  likeCount?: number;
}

const HeaderComic: React.FC<HeaderComicProps> = ({
  title,
  banner,
  description,
  playUrl,
  onPlay,
  type,
  firstChapterId,
  lastChapterId,
  loading = false,
  author,
  genre = [],
  status,
  rating,
  totalChapters,
  views,
  episodeTotal,
  likeCount,
}) => {
  const descRef = useRef<HTMLParagraphElement>(null);
  const [showToggle, setShowToggle] = useState(false);
  const bannerPath = typeof banner === "string" ? banner : banner?.path || "";

  // Check if description should show toggle
  useEffect(() => {
    if (descRef.current && description) {
      const element = descRef.current;
      setShowToggle(element.scrollHeight > element.clientHeight);
    }
  }, [description]);

  return (
    <div className="featured-img">
      <div className="flex flex-col md:flex-row gap-4 md:gap-6 mb-4 px-6">
        {/* Image Section */}

        <div className="relative rounded-lg overflow-hidden aspect-[2/3] max-w-[200px] mx-auto md:mx-0 shadow-lg">
          <ImageComponent
            src={bannerPath}
            alt={title}
            className="w-full h-full object-cover"
          />
          {/* Book-like border effect */}
          <div className="absolute inset-0 rounded-lg border border-white/10 pointer-events-none"></div>
          {/* Subtle gradient overlay for depth */}
          <div className="absolute inset-0 bg-gradient-to-br from-transparent via-transparent to-black/20 pointer-events-none"></div>
        </div>

        {/* Content Section */}
        <div className="w-full md:w-2/3 lg:w-3/4 flex flex-col justify-center">
          {/* Title */}
          <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-4 leading-tight">
            {title}
          </h1>

          {/* Meta Information */}
          <div className="mb-4 space-y-2">
           
            {genre && genre.length > 0 && (
              <div className="hidden md:flex items-center gap-2 text-gray-300">
                <span className="text-sm font-medium text-gray-400">
                  Tags:
                </span>
                <div className="flex flex-wrap gap-1">
                  {genre.map((g, index) => (
                    <span
                      key={index}
                      className="text-xs bg-primary/20 text-primary px-2 py-1 rounded-full"
                    >
                      {g}
                    </span>
                  ))}
                </div>
              </div>
            )}
            <div className="hidden md:flex list-count w-full py-2 md:py-4">
              <ul className="flex gap-3 md:gap-12 text-xs md:text-base">
                <li className="flex items-center gap-1">
                  <img
                    src="/assets/episode.svg"
                    alt="Episode"
                    className="w-4 h-4 md:w-auto md:h-auto"
                  />
                  <span>{episodeTotal || 0} Chương</span>
                </li>
                <li className="flex items-center gap-1">
                  <img
                    src="/assets/like.svg"
                    alt="Like"
                    className="w-4 h-4 md:w-auto md:h-auto"
                  />
                  <span>{likeCount || 0} Lượt thích</span>
                </li>
                <li className="flex items-center gap-1">
                  <img
                    src="/assets/rating.svg"
                    alt="Rating"
                    className="w-4 h-4 md:w-auto md:h-auto"
                  />
                  <span>{rating?.toFixed(1) || 0}/10</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-3">
            {firstChapterId && (
              <button
                className="bg-primary hover:bg-primary-dark px-6 py-3 rounded-full text-white font-semibold transition flex items-center gap-2"
                onClick={() => firstChapterId && onPlay?.(firstChapterId)}
                disabled={!firstChapterId}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 22"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M15.2089 10.6335C15.7599 11.0429 15.7441 11.8729 15.178 12.261L1.56546 21.5936C0.901852 22.0485 5.35697e-07 21.5734 5.16316e-07 20.7688L4.79044e-08 1.32229C2.81054e-08 0.500314 0.936598 0.0293746 1.59639 0.51959L15.2089 10.6335Z"
                    fill="white"
                  />
                </svg>
                <span>Đọc truyện</span>
              </button>
            )}
            {lastChapterId && (
              <button
                className="bg-[#6B87F9] hover:bg-blue-600 px-6 py-3 rounded-full text-white font-semibold transition flex items-center gap-2"
                onClick={() => lastChapterId && onPlay?.(lastChapterId)}
                disabled={!lastChapterId}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 50 50"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M43.5884 31.7603L39.0579 24.8644L43.5421 18.2498C43.6486 18.0922 43.7067 17.9119 43.7105 17.7274C43.7143 17.5429 43.6636 17.3608 43.5637 17.1997C43.4637 17.0387 43.3181 16.9044 43.1417 16.8107C42.9653 16.717 42.7645 16.6673 42.5598 16.6665H6.47807C5.86504 16.6665 5.27711 16.886 4.84363 17.2767C4.41015 17.6674 4.16663 18.1973 4.16663 18.7498L4.16663 31.2498C4.16663 31.8024 4.41015 32.3323 4.84363 32.723C5.27711 33.1137 5.86504 33.3332 6.47807 33.3332H42.5944C42.7984 33.3331 42.9988 33.2844 43.1751 33.192C43.3515 33.0996 43.4975 32.9668 43.5985 32.807C43.6995 32.6473 43.7517 32.4663 43.7499 32.2824C43.7481 32.0986 43.6924 31.9184 43.5884 31.7603ZM16.3133 28.3123H15.0189L11.2512 23.7915V28.3228H9.94524V21.8748H11.2512L15.0304 26.4061V21.8748H16.3248L16.3133 28.3123ZM23.629 22.9165H19.5493V24.4686H23.2476V25.5103H19.5493V27.1873H23.629V28.229H18.2433V21.8748H23.6175L23.629 22.9165ZM33.21 28.2915H31.904L30.1126 23.5103L28.3213 28.3123H27.0268L24.7154 21.8748H26.1254L27.6509 26.4894L29.4076 21.8748H30.8176L32.505 26.4894L34.0421 21.8748H35.4636L33.21 28.2915Z"
                    fill="white"
                  />
                </svg>
                <span>Chương mới</span>
              </button>
            )}
            {!firstChapterId && !lastChapterId && !loading && (
              <div className="bg-gray-700 text-white px-6 py-3 rounded font-semibold opacity-80">
                Đang phát hành
              </div>
            )}
            {loading && (
              <div className="flex items-center gap-2">
                <svg
                  className="animate-spin h-5 w-5 text-primary"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                  ></path>
                </svg>
                <span className="text-gray-400">Đang tải...</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HeaderComic;
